import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
  ferryApi,
  type FerryCapacity,
  type FerryCarrier,
  type FerryCredential,
  type FerryRoute,
  type FerryRouteFilter,
  type FerrySailing,
  type FerryVehicleType,
} from '../client';
import { ferryKeys } from './useKeys';

const DAY = 24 * 60 * 60 * 1000;

/** Carriers known to Ferry. Seeded data; a day is a safe stale time. */
export function useFerryCarriers(
  options?: Omit<UseQueryOptions<FerryCarrier[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<FerryCarrier[], Error>({
    queryKey: ferryKeys.carriers(),
    queryFn: () => ferryApi.getCarriers(),
    staleTime: DAY,
    gcTime: Infinity,
    ...options,
  });
}

/** A carrier's routes, optionally one category or only active ones. Synced nightly, so cached for a day. */
export function useFerryRoutes(
  carrierId: string | null,
  filter: FerryRouteFilter = {},
  options?: Omit<UseQueryOptions<FerryRoute[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<FerryRoute[], Error>({
    queryKey: carrierId ? ferryKeys.routes(carrierId, filter) : ferryKeys.all,
    queryFn: () => ferryApi.getRoutes(carrierId!, filter),
    enabled: !!carrierId,
    staleTime: DAY,
    gcTime: Infinity,
    ...options,
  });
}

const ACTIVE_ROUTES: FerryRouteFilter = { isActive: true };

/**
 * Every carrier's routes in one read — the suite's crossing list; active ones
 * unless the filter says otherwise. Synced nightly, so cached for a day.
 */
export function useFerryAllRoutes(
  filter: FerryRouteFilter = ACTIVE_ROUTES,
  options?: Omit<UseQueryOptions<FerryRoute[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<FerryRoute[], Error>({
    queryKey: ferryKeys.routes(null, filter),
    queryFn: () => ferryApi.getRoutes(null, filter),
    staleTime: DAY,
    gcTime: Infinity,
    ...options,
  });
}

export function useFerryVehicleTypes(
  carrierId: string | null,
  isActive?: boolean,
  options?: Omit<UseQueryOptions<FerryVehicleType[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<FerryVehicleType[], Error>({
    queryKey: carrierId ? ferryKeys.vehicleTypes(carrierId, isActive) : ferryKeys.all,
    queryFn: () => ferryApi.getVehicleTypes(carrierId!, isActive),
    enabled: !!carrierId,
    staleTime: DAY,
    gcTime: Infinity,
    ...options,
  });
}

/**
 * Sailings on one route in a UTC window. The timetable is synced nightly, so a
 * few minutes of staleness is fine; pass the window as fixed strings (not a
 * fresh `new Date()` per render) or the key never settles.
 */
export function useFerrySailings(
  carrierId: string | null,
  routeCode: string | null,
  fromUtc: string | null,
  toUtc: string | null,
  options?: Omit<UseQueryOptions<FerrySailing[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  const enabled = !!carrierId && !!routeCode && !!fromUtc && !!toUtc;
  return useQuery<FerrySailing[], Error>({
    queryKey: enabled
      ? ferryKeys.sailings(carrierId!, routeCode!, fromUtc!, toUtc!)
      : ferryKeys.all,
    queryFn: () => ferryApi.getSailings(carrierId!, routeCode!, fromUtc!, toUtc!),
    enabled,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * The organisation's capacity on one route in a UTC window: free blocks per
 * class on each sailing, plus the rows behind them. Mirrored hourly at Ferry
 * and re-read after every booking change, so keep this short-lived.
 */
export function useFerryCapacity(
  organisationId: string | null,
  carrierId: string | null,
  routeCode: string | null,
  fromUtc: string | null,
  toUtc: string | null,
  options?: Omit<UseQueryOptions<FerryCapacity, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  const enabled = !!organisationId && !!carrierId && !!routeCode && !!fromUtc && !!toUtc;
  return useQuery<FerryCapacity, Error>({
    queryKey: enabled
      ? ferryKeys.capacity(organisationId!, carrierId!, routeCode!, fromUtc!, toUtc!)
      : ferryKeys.capacityAll,
    queryFn: () => ferryApi.getCapacity(organisationId!, carrierId!, routeCode!, fromUtc!, toUtc!),
    enabled,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

/**
 * The organisation's accounts at the carriers (never the secret). A booking
 * must name one only when the organisation holds more than one at the carrier.
 */
export function useFerryCredentials(
  organisationId: string | null,
  carrierId?: string,
  options?: Omit<UseQueryOptions<FerryCredential[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<FerryCredential[], Error>({
    queryKey: organisationId ? ferryKeys.credentials(organisationId, carrierId) : ferryKeys.all,
    queryFn: () => ferryApi.getCredentials(organisationId!, carrierId),
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}
